import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';

/**
 * Engagements / Projects surface (T-0022 / E01-F02).
 *
 * The backend models the entity as `Project`. Each Project has a stable
 * `engagement_id` that every downstream resource (documents, registers,
 * chat, readiness, archive) keys on. The frontend uses `engagement_id`
 * everywhere — pages live under `/engagements/:engagementId`. To resolve
 * an engagement_id back to its Project, the frontend currently has to
 * call `GET /api/v1/projects/:id` with the **project id**, not the
 * engagement id. The shapes overlap enough that v1 fetches the Project
 * by id and trusts the engagement_id field; the dashboard list path
 * surfaces both ids per row.
 *
 * **Known gap (flagged):** there is no `GET /api/v1/projects` list
 * endpoint yet. The dashboard discovers engagements by walking the
 * Client list and reading the per-Client project arrays — but those
 * arrays aren't currently exposed by `GET /api/v1/clients/:id` either.
 * For the scaffold the dashboard accepts an empty list and surfaces a
 * placeholder; a follow-up backend ticket needs to either:
 *   (a) add `GET /api/v1/engagements?for_user=me` (the original spec
 *       request); or
 *   (b) include `projects: [...]` on `GET /api/v1/clients/:id`.
 */

export const ProjectDetailSchema = z.object({
  id: z.string().uuid(),
  engagement_id: z.string().uuid(),
  client_id: z.string().uuid(),
  name: z.string(),
  modules: z.object({
    contract_compliance: z.boolean(),
    health_check: z.boolean(),
    go_live_readiness: z.boolean(),
    // Optional so a response from an API build that predates the
    // Selection module still parses.
    selection: z.boolean().optional(),
  }),
  status: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type ProjectDetail = z.infer<typeof ProjectDetailSchema>;

export function getProject(projectId: string): Promise<ProjectDetail> {
  return apiClient(`/api/v1/projects/${encodeURIComponent(projectId)}`, ProjectDetailSchema);
}

export function useProjectQuery(projectId: string | undefined) {
  return useQuery({
    queryKey: ['projects', 'detail', projectId] as const,
    queryFn: () => getProject(projectId!),
    enabled: Boolean(projectId),
  });
}

/**
 * Engagement DTO returned by the T-0091 banner attach / clear endpoints
 * (the list endpoint also uses this subset). Mirrors
 * `apps/api/src/services/engagements.ts → EngagementDTO`. Use
 * `passthrough()` so a future field addition on the server doesn't fail
 * the schema check.
 *
 * T-0128: `start_date` / `planned_end` / `client_pm` were added to the
 * Project schema with nullable columns; pre-T-0128 engagements return
 * `null` for all three.
 */
export const EngagementDtoSchema = z
  .object({
    id: z.string().uuid(),
    project_id: z.string().uuid(),
    project_name: z.string(),
    client_id: z.string().uuid(),
    client_name: z.string(),
    name: z.string(),
    status: z.string(),
    modules_enabled: z.array(z.string()),
    banner_image_url: z.string().nullable(),
    start_date: z.string().nullable().optional(),
    planned_end: z.string().nullable().optional(),
    client_pm: z.string().nullable().optional(),
    created_at: z.string(),
  })
  .passthrough();
export type EngagementDto = z.infer<typeof EngagementDtoSchema>;

// ===========================================================================
// T-0119 hero DTO extensions — extra fields surfaced on
// GET /api/v1/engagements/:engagement_id only (not on the list endpoint).
// All five fields are always present so the UI can render empty-state
// variants per field without optional-chaining gymnastics. `null` and `[]`
// signal "not applicable", not "missing".
// ===========================================================================

export const RECENT_SIGNAL_KINDS = [
  'posture_change',
  'override',
  'proposal',
  'rfi_transition',
  'deviation',
  'score_change',
  'document',
  'deliverable',
  'contract',
] as const;
export type RecentSignalKind = (typeof RECENT_SIGNAL_KINDS)[number];

export const RECENT_SIGNAL_SEVERITIES = ['critical', 'high', 'medium', 'low', 'info'] as const;
export type RecentSignalSeverity = (typeof RECENT_SIGNAL_SEVERITIES)[number];

export const RecentSignalSchema = z.object({
  kind: z.enum(RECENT_SIGNAL_KINDS),
  severity: z.enum(RECENT_SIGNAL_SEVERITIES),
  summary: z.string(),
  occurred_at: z.string(),
  source_ref: z.object({ type: z.string(), id: z.string() }).nullable().optional(),
});
export type RecentSignal = z.infer<typeof RecentSignalSchema>;

export const BudgetRollupSummarySchema = z.object({
  bac: z.number(),
  ac: z.number(),
  ev: z.number(),
  cpi: z.number(),
  spi: z.number(),
  projected: z.number(),
  variance_state: z.enum(['ok', 'warn', 'critical']),
});
export type BudgetRollupSummary = z.infer<typeof BudgetRollupSummarySchema>;

export const AiCostSummarySchema = z.object({
  cap: z.number().nullable(),
  spent: z.number(),
  percent_of_cap: z.number().nullable(),
});
export type AiCostSummary = z.infer<typeof AiCostSummarySchema>;

export const PhasePathStepSchema = z.object({
  label: z.string(),
  status: z.enum(['done', 'current', 'upcoming']),
});
export type PhasePathStep = z.infer<typeof PhasePathStepSchema>;

export const EngagementDetailDtoSchema = EngagementDtoSchema.and(
  z.object({
    recent_signals: z.array(RecentSignalSchema),
    budget_rollup: BudgetRollupSummarySchema.nullable(),
    ai_cost_summary: AiCostSummarySchema.nullable(),
    open_issues_count: z.number().int().nonnegative(),
    // Past-due, unresolved contract deliverables (same rule as the
    // portfolio column). Optional so an older API build that predates the
    // field still parses; the overview renders "—" when absent.
    overdue_count: z.number().int().nonnegative().optional(),
    phase_path: z.array(PhasePathStepSchema),
  }),
);
export type EngagementDetailDto = z.infer<typeof EngagementDetailDtoSchema>;

/**
 * GET /api/v1/engagements/:engagement_id — caller-scoped engagement fetch.
 * Added by the T-0091 backend pass; T-0119 widened the response with the
 * five hero sub-resolvers (recent_signals, budget_rollup, ai_cost_summary,
 * open_issues_count, phase_path) so the T-0097 overview hero can render
 * without N+1 follow-up reads.
 */
export function getEngagement(engagementId: string): Promise<EngagementDetailDto> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}`,
    EngagementDetailDtoSchema,
  );
}

export function useEngagementQuery(engagementId: string | undefined) {
  return useQuery({
    queryKey: ['engagements', 'detail', engagementId] as const,
    queryFn: () => getEngagement(engagementId!),
    enabled: Boolean(engagementId),
    // Banner / name / client_name don't change often; 30s keeps the hero
    // crisp across tab navigation without thrashing the API on every click.
    staleTime: 30_000,
  });
}

/**
 * POST /api/v1/engagements/:engagement_id/banner — attach a previously
 * uploaded document as the engagement's hero banner. The document upload
 * happens via the existing T-0040 chunked-upload flow
 * (`documents.ts → initiateUpload` + `putUploadChunk` + `completeUpload`);
 * the resulting `document_id` is what this endpoint expects.
 *
 * Server-side validation:
 *   - document must belong to the same engagement (cross-engagement → 404)
 *   - latest version must have finished uploading (`upload_status` 'stored' or 'parsed')
 *   - mime_type must be one of jpeg/png/webp/gif
 */
export function setEngagementBanner(
  engagementId: string,
  documentId: string,
): Promise<EngagementDto> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/banner`,
    EngagementDtoSchema,
    { method: 'POST', body: { document_id: documentId } },
  );
}

/**
 * DELETE /api/v1/engagements/:engagement_id/banner — clear the banner.
 * Idempotent on the server: clearing an already-null banner is a 200.
 */
export function clearEngagementBanner(engagementId: string): Promise<EngagementDto> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/banner`,
    EngagementDtoSchema,
    { method: 'DELETE' },
  );
}

/**
 * GET /api/v1/engagements — caller-scoped engagements list.
 *
 * Backed by `apps/api/src/services/engagements.ts → listEngagements`. The
 * server enforces caller-scope (membership + permission overrides), so
 * passing `client_id` as a query parameter further narrows the result to
 * one client. Used by the T-0108 clients table + detail view to surface
 * "Active engagements" KPI counts and per-client engagement lists.
 */
export const EngagementsListResponseSchema = z.object({
  engagements: z.array(EngagementDtoSchema),
  next_cursor: z.string().optional(),
});
export type EngagementsListResponse = z.infer<typeof EngagementsListResponseSchema>;

export interface ListEngagementsOptions {
  /** Restrict to one client's engagements. */
  clientId?: string;
  /** Filter on Project.status — 'setup' | 'active' | 'archived' (or 'closed' alias). */
  status?: string;
  /** Name search, case-insensitive substring (T-0227 command search). */
  q?: string;
  /** Opaque cursor from a prior page. */
  cursor?: string;
  /** Page size override (default 50, max 200). */
  limit?: number;
}

export function listEngagements(
  opts: ListEngagementsOptions = {},
): Promise<EngagementsListResponse> {
  const query: Record<string, string | number | undefined> = {};
  if (opts.clientId !== undefined) query.client_id = opts.clientId;
  if (opts.status !== undefined) query.status = opts.status;
  if (opts.q !== undefined) query.q = opts.q;
  if (opts.cursor !== undefined) query.cursor = opts.cursor;
  if (opts.limit !== undefined) query.limit = opts.limit;
  return apiClient('/api/v1/engagements', EngagementsListResponseSchema, { query });
}

export function useEngagementsListQuery(opts: ListEngagementsOptions = {}) {
  return useQuery({
    // The query key carries the option fingerprint so concurrent fetches
    // (firm-wide list on the clients page vs. per-client list on the
    // detail view) don't share results.
    queryKey: ['engagements', 'list', opts] as const,
    queryFn: () => listEngagements(opts),
    staleTime: 30_000,
  });
}

/**
 * Create-project payload (T-0117 wizard, steps 2-3). The backend models
 * a Project 1:1 with an engagement_id; the wizard mints both via a single
 * POST. Mirrors `apps/api/src/services/projects.ts → ProjectCreateInput`.
 *
 * Modules map: the on-the-wire field names mirror the underlying DB
 * (`module_contract_compliance` etc.) — the wizard exposes them as
 * `contract / health / golive` and converts to the backend shape here.
 */
export interface CreateProjectInput {
  name: string;
  modules: {
    contract_compliance?: boolean;
    health_check?: boolean;
    go_live_readiness?: boolean;
    selection?: boolean;
    /** Discovery (E12). The API turns Selection on with it (T-0171). */
    discovery?: boolean;
  };
  /** T-0128: optional engagement metadata captured by the wizard's confirm step. */
  start_date?: string | null;
  planned_end?: string | null;
  client_pm?: string | null;
}

export const CreatedProjectSchema = z.object({
  id: z.string().uuid(),
  engagement_id: z.string().uuid(),
  status: z.string(),
  modules: z.object({
    contract_compliance: z.boolean(),
    health_check: z.boolean(),
    go_live_readiness: z.boolean(),
    selection: z.boolean().optional(),
  }),
});
export type CreatedProject = z.infer<typeof CreatedProjectSchema>;

/**
 * POST /api/v1/clients/:client_id/projects — minted engagement. The wizard
 * supplies `Idempotency-Key` so a network retry doesn't duplicate the
 * project. On success every engagement list (firm-wide + per-client) is
 * invalidated so the dashboard, the clients table, and the client detail
 * view all refresh immediately.
 */
export function createProject(
  clientId: string,
  input: CreateProjectInput,
  idempotencyKey?: string,
): Promise<CreatedProject> {
  return apiClient(
    `/api/v1/clients/${encodeURIComponent(clientId)}/projects`,
    CreatedProjectSchema,
    {
      method: 'POST',
      body: input,
      ...(idempotencyKey ? { idempotencyKey } : {}),
    },
  );
}

export function useCreateProjectMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      clientId,
      input,
      idempotencyKey,
    }: {
      clientId: string;
      input: CreateProjectInput;
      idempotencyKey?: string;
    }) => createProject(clientId, input, idempotencyKey),
    onSuccess: () => {
      // The engagement list keys carry an opts fingerprint, so invalidate
      // every variant; React Query treats the prefix as a wildcard.
      void qc.invalidateQueries({ queryKey: ['engagements', 'list'] });
      void qc.invalidateQueries({ queryKey: ['portfolio'] });
    },
  });
}

// ===========================================================================
// Engagement details edit — PATCH /api/v1/projects/:id
// ===========================================================================

const PatchedProjectSchema = z
  .object({
    id: z.string().uuid(),
    engagement_id: z.string().uuid(),
    updated_at: z.string(),
  })
  .passthrough();

/**
 * Fields the overview's "Edit details" modal can change. `undefined`
 * leaves a field alone; `null` clears a date or the client PM. `status`
 * is what moves the Phase tile out of Setup (setup → active).
 *
 * `modules` carries only the flags being changed. The API keeps its rules:
 * Discovery on turns Selection on, Selection cannot go off while Discovery
 * stays on, and at least one module must stay on.
 */
export interface UpdateEngagementDetailsInput {
  status?: 'setup' | 'active' | 'archived';
  start_date?: string | null;
  planned_end?: string | null;
  client_pm?: string | null;
  modules?: {
    contract_compliance?: boolean;
    health_check?: boolean;
    go_live_readiness?: boolean;
    selection?: boolean;
    discovery?: boolean;
  };
}

export function updateEngagementDetails(projectId: string, input: UpdateEngagementDetailsInput) {
  return apiClient(`/api/v1/projects/${encodeURIComponent(projectId)}`, PatchedProjectSchema, {
    method: 'PATCH',
    body: input,
  });
}

/**
 * The endpoint is keyed by PROJECT id (not engagement id). On success the
 * engagement detail (facts strip + phase path), every engagement list,
 * and the portfolio are invalidated — go-live date and status show up on
 * all three, and the sidebar's module entries read `modules_enabled` from
 * the portfolio.
 */
export function useUpdateEngagementDetailsMutation(
  projectId: string | undefined,
  engagementId: string | undefined,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateEngagementDetailsInput) => updateEngagementDetails(projectId!, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['engagements', 'detail', engagementId] });
      void qc.invalidateQueries({ queryKey: ['engagements', 'list'] });
      void qc.invalidateQueries({ queryKey: ['portfolio'] });
    },
  });
}
